import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MessageCircle } from "lucide-react";
import { IMAGES, WHATSAPP_URL, scrollToId } from "../lib/site";

const LINES = [
  { text: "Il parquet,", cls: "" },
  { text: "posato", cls: "italic text-honey" },
  { text: "a regola d'arte.", cls: "" },
];

const Hero = () => {
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 700], [0, 160]);
  const yText = useTransform(scrollY, [0, 700], [0, -70]);

  return (
    <section
      id="top"
      className="grain relative flex min-h-[100svh] items-end overflow-hidden bg-walnut"
      data-testid="hero-section"
    >
      <motion.div style={{ y: yImg }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Pavimento in parquet posato a regola d'arte"
          className="h-[115%] w-full object-cover opacity-45"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-walnut via-walnut/55 to-walnut/30" />
      </motion.div>

      <motion.div
        style={{ y: yText }}
        className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-24 pt-40 md:px-10"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
          className="mb-6 font-sans text-xs font-light uppercase tracking-[0.45em] text-honey"
          data-testid="hero-overline"
        >
          RUS Srl · Posa parquet · Roma e provincia
        </motion.p>

        <h1
          className="font-display text-5xl font-medium leading-[1.02] text-cream sm:text-6xl lg:text-8xl"
          data-testid="hero-headline"
        >
          {LINES.map((l, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{
                  delay: 0.35 + i * 0.16,
                  duration: 1,
                  ease: [0.16, 1, 0.3, 1],
                }}
                className={`block ${l.cls}`}
              >
                {l.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.8 }}
          className="mt-8 max-w-xl font-sans text-base font-light leading-relaxed text-sand/85 md:text-lg"
        >
          Ci occupiamo di posa in opera del tuo parquet: a correre, spina
          italiana, spina francese e ungherese. E non finisce qui: lamatura e
          posa di SPC, laminati e PVC. Prezzi chiari al metro quadro,
          confermati dopo un sopralluogo gratuito a casa tua.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.35, duration: 0.8 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => scrollToId("sopralluogo")}
            data-testid="hero-cta-sopralluogo"
            className="group rounded-full bg-honey px-7 py-3.5 font-sans text-sm font-medium tracking-wide text-ink transition-colors duration-300 hover:bg-cream"
          >
            Prenota il sopralluogo gratuito
          </button>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-cta-whatsapp"
            className="flex items-center gap-2 rounded-full border border-sand/40 px-7 py-3.5 font-sans text-sm font-light text-sand transition-colors duration-300 hover:border-honey hover:text-honey"
          >
            <MessageCircle size={16} strokeWidth={1.8} />
            Scrivici su WhatsApp
          </a>
        </motion.div>
      </motion.div>

      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        onClick={() => scrollToId("manifesto")}
        data-testid="hero-scroll-indicator"
        aria-label="Scorri"
        className="absolute bottom-6 right-6 z-10 text-sand/60 transition-colors hover:text-honey md:right-10"
      >
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
          className="block"
        >
          <ArrowDown size={22} strokeWidth={1.4} />
        </motion.span>
      </motion.button>
    </section>
  );
};

export default Hero;
